/**
 * TaskEditPopup Component
 *
 * Modal popup for editing card details (title, description, metadata)
 * Similar to ProfilePopup in terms of user interaction pattern
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { BoardTask } from '@/types/models/BoardTask';
import { BoardStatusWithCards } from '@/types/board-config.types';
import { BoardTag } from '@/types/enums/BoardTag';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { sizing } from '@/styles/design-tokens';
import { Icon, IconName } from '@/icons';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: handleOpenCard uses { toast: false } for silent navigation fetch (GET_TASK)
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
import { ShareButton } from '@/components/core/ShareButton';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { BoardTaskPriority } from '@/types/enums/BoardTaskPriority';
import { useOrgUsersQuery, useCommentsQuery } from '@/hooks/queries';
import { MAX_POMODORO_COUNT } from '@/business/constants/board-task-limits';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import type {
  UpdateTaskInput,
  UpdateTaskOutput,
  CreateTaskInput,
  CreateTaskOutput,
  CreateCommentInput,
  CreateCommentOutput,
  GetTaskInput,
  GetTaskOutput,
} from '@/types/generated';
import type { CommentWithUserData } from '@/types/action-outputs';
import { MentionInput, CommentsList } from '@/components/core/Comments';
import { Alert, useAlert } from '@/components/core/Alert';
import { DeletedEntityBanner } from '@/components/core/DeletedEntityBanner';
import { useToast } from '@/hooks/useToast';
import SubTasksSection from './SubTasksSection';
import { logger } from '@/utils/logger';
import { parseDateToLocalString, formatDate } from '@/utils/date';

interface TaskEditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  card: BoardTask | null;
  statuses: BoardStatusWithCards[];
  onCardUpdated: () => void;
  onSubcardsCreated?: (newCards: BoardTask[]) => void;
  organizationId: string;
  /** Board ID for creating new cards */
  boardId?: string;
  /** Create mode - when true, creates a new card instead of editing */
  isCreateMode?: boolean;
  /** Whether this card is frozen (past week, non-admin) */
  isFrozen?: boolean;
  /** Whether start_date field should be disabled (non-admin in edit mode) */
  isStartDateDisabled?: boolean;
  /** Whether user is admin (for delete action) */
  isAdmin?: boolean;
  /** Tag of the board this card belongs to */
  boardTag?: string | null;
  /** Called when a related card should be opened — caller replaces the current card */
  onNavigateToCard?: (card: BoardTask) => void;
  /** When true, comment input is shown even if the card is read-only (e.g. mentor on a student's cohort copy) */
  isCommentAllowed?: boolean;
  /** When true, structural fields (title, description, priority, deadline) are disabled; pomodoros still editable */
  isEditLocked?: boolean;
  /** When true, current user is not this card's assignee/assigned mentor/admin — read-only, mirrors backend's isCardAssignee/isCardMentor rejection */
  isEditForbidden?: boolean;
}

// Convert an ISO 8601 / Date value (or undefined) to the value format expected by
// `<input type="datetime-local">` ("YYYY-MM-DDTHH:MM" in local time).
function isoToLocalDatetimeInput(value?: Date | string | null): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Convert the date input value back to a date-only string (YYYY-MM-DD).
// Empty string is preserved as empty string (the backend treats "" as "clear deadline").
function localDatetimeInputToIso(value: string): string {
  return value; // already YYYY-MM-DD, backend accepts date-only
}

export default function TaskEditPopup({
  isOpen,
  onClose,
  card,
  statuses,
  onCardUpdated,
  onSubcardsCreated,
  organizationId,
  boardId,
  isCreateMode = false,
  isFrozen = false,
  isStartDateDisabled = false,
  isAdmin = false,
  boardTag,
  onNavigateToCard,
  isCommentAllowed = false,
  isEditLocked = false,
  isEditForbidden = false,
}: TaskEditPopupProps) {
  const colors = useThemeColors();
  const toast = useToast();
  const { openModal, closeModal, registerLoadingGate } = useModalManager();

  // Treat deleted cards as frozen (read-only)
  const isCardDeleted = !!card?.deleted_at;
  const isReadOnly = isFrozen || isCardDeleted || isEditForbidden;
  // Structural fields locked by column config (title, description, priority, deadline); pomodoros still editable
  const isStructuralLocked = isEditLocked && !isCreateMode;

  // Determine if the frozen card is genuinely from a past week
  const isFrozenFromPastWeek =
    isFrozen &&
    (() => {
      if (!card?.start_date) return false;
      const cardDate = new Date(card.start_date);
      if (isNaN(cardDate.getTime())) return false;
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0=Sun
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - dayOfWeek);
      weekStart.setHours(0, 0, 0, 0);
      return cardDate < weekStart;
    })();

  // Cohort detection
  const isCohortBoard = boardTag === BoardTag.COHORT;
  // Sub-card: distributed copy that has a source reference
  const isCohortCopy = !isCreateMode && !!card?.metadata?.cohort_source_id;
  // Creator's original card on the cohort board (not a distributed copy)
  const isCohortCreatorCard = isCohortBoard && !isCohortCopy && !isCreateMode;
  // Fields fanned out from creator → always locked on sub-cards regardless of role
  const isCohortContentLocked = isCohortCopy;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('');
  const [estimatedPomodoros, setEstimatedPomodoros] = useState<string>('');
  const [actualPomodoros, setActualPomodoros] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  // deadline kept in `<input type="date">` format ("YYYY-MM-DD"). Empty string means "no deadline".
  const [deadline, setDeadline] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track initial values for dirty state detection
  const [initialTitle, setInitialTitle] = useState('');
  const [initialDescription, setInitialDescription] = useState('');
  const [initialPriority, setInitialPriority] = useState<string>('');
  const [initialEstimatedPomodoros, setInitialEstimatedPomodoros] =
    useState<string>('');
  const [initialActualPomodoros, setInitialActualPomodoros] =
    useState<string>('');
  const [initialStartDate, setInitialStartDate] = useState<string>('');
  const [initialDeadline, setInitialDeadline] = useState<string>('');

  // Comments state

  const [commentContent, setCommentContent] = useState('');
  const [commentsOffset, setCommentsOffset] = useState(0);

  // Retry callback stored when save fails
  const [errorRetry, setErrorRetry] = useState<(() => void) | null>(null);

  // Ref to prevent duplicate submissions (faster than state)
  const isSubmittingRef = useRef(false);
  const isSavingRef = useRef(false);
  const lastSubmittedContentRef = useRef<string>('');

  // Unsaved changes confirmation alert
  const unsavedChangesAlert = useAlert();

  const deleteConfirmAlert = useAlert();

  const handleOpenCard = async (taskId: string) => {
    if (!onNavigateToCard) return;
    const result = await actionOrchestrator.execute<
      GetTaskInput,
      GetTaskOutput
    >(Action.GET_TASK, { task_id: taskId }, { toast: false });
    if (result.code === ResponseCode.SUCCESS && result.data?.card) {
      const c = result.data.card;
      onNavigateToCard({
        id: c.id,
        board_id: c.board_id,
        organization_id: organizationId,
        status_id: c.status_id,
        title: c.title,
        description: c.description,
        metadata: c.metadata,
        created_by: c.created_by,
        created_at: c.created_at,
        priority: c.priority as BoardTask['priority'],
        estimated_pomodoros: c.estimated_pomodoros,
        actual_pomodoros: c.actual_pomodoros,
        start_date: c.start_date,
      });
    }
  };

  // Query hooks replace manual state + useEffect loads
  const { data: orgUsersData, isLoading: isLoadingOrgUsers } = useOrgUsersQuery(
    isOpen ? organizationId : undefined
  );
  const orgUsers = orgUsersData?.users ?? [];

  const {
    data: commentsData,
    isLoading: isLoadingComments,
    isError: isCommentsError,
  } = useCommentsQuery(isCreateMode ? undefined : card?.id, commentsOffset);

  const comments = useMemo<CommentWithUserData[]>(() => {
    const raw = commentsData?.comments ?? [];
    return [...raw].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [commentsData]);

  const hasMoreComments = commentsData?.has_more ?? false;

  // Use orchestrator hook for automatic toast notifications
  const { execute: executeUpdateCard } = useOrchestrator<
    UpdateTaskInput,
    UpdateTaskOutput
  >(Action.UPDATE_TASK);

  const { execute: executeCreateCard } = useOrchestrator<
    CreateTaskInput,
    CreateTaskOutput
  >(Action.CREATE_TASK);

  const { execute: executeCreateComment, isLoading: isSubmittingComment } =
    useOrchestrator<CreateCommentInput, CreateCommentOutput>(
      Action.CREATE_COMMENT
    );

  const { execute: executeDeleteCard, isLoading: isDeleting } = useOrchestrator<
    { task_id: string; board_id?: string },
    unknown
  >(Action.DELETE_TASK);

  // Get current status info
  const currentStatus = card
    ? statuses.find(s => s.id === card.status_id)
    : null;

  // Get creator user info
  const creatorUser = card?.created_by
    ? orgUsers.find(u => u.id === card.created_by)
    : null;

  // Reset deduplication refs when modal closes
  useEffect(() => {
    if (!isOpen) {
      isSubmittingRef.current = false;
      isSavingRef.current = false;
      lastSubmittedContentRef.current = '';
    }
  }, [isOpen, card?.id]);

  // Initialize form fields when card changes or popup opens
  useEffect(() => {
    if (!isOpen) return;

    if (isCreateMode) {
      const today = parseDateToLocalString(null);
      setTitle('');
      setDescription('');
      setPriority(BoardTaskPriority.MEDIUM);
      setEstimatedPomodoros('');
      setActualPomodoros('');
      setStartDate(today);
      setDeadline('');
      setInitialTitle('');
      setInitialDescription('');
      setInitialPriority(BoardTaskPriority.MEDIUM);
      setInitialEstimatedPomodoros('');
      setInitialActualPomodoros('');
      setInitialStartDate(today);
      setInitialDeadline('');
      setError(null);
      setErrorRetry(null);
      setCommentsOffset(0);
      return;
    }

    if (card) {
      const cardTitle = card.title || '';
      const cardDescription = card.description || '';
      const cardPriority = card.priority || '';
      const cardEstimatedPomodoros = card.estimated_pomodoros
        ? String(card.estimated_pomodoros)
        : '';
      const cardActualPomodoros = card.actual_pomodoros
        ? String(card.actual_pomodoros)
        : '';
      const cardStartDate = parseDateToLocalString(card.start_date);
      const cardDeadline = isoToLocalDatetimeInput(card.deadline);

      setTitle(cardTitle);
      setDescription(cardDescription);
      setPriority(cardPriority);
      setEstimatedPomodoros(cardEstimatedPomodoros);
      setActualPomodoros(cardActualPomodoros);
      setStartDate(cardStartDate);
      setDeadline(cardDeadline);
      setInitialTitle(cardTitle);
      setInitialDescription(cardDescription);
      setInitialPriority(cardPriority);
      setInitialEstimatedPomodoros(cardEstimatedPomodoros);
      setInitialActualPomodoros(cardActualPomodoros);
      setInitialStartDate(cardStartDate);
      setInitialDeadline(cardDeadline);
      setError(null);
      setErrorRetry(null);
      setCommentsOffset(0); // Reset offset when card changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally using card?.id instead of full card object to avoid over-triggering
  }, [card?.id, isOpen, isCreateMode]);

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.CARD_EDIT);
    } else {
      closeModal(ModalId.CARD_EDIT);
    }
  }, [isOpen, openModal, closeModal]);

  // Register loading gate for comments
  // This allows CommentsList to wait for comments to load before highlighting
  useEffect(() => {
    registerLoadingGate('comments-loaded', isLoadingComments);
  }, [isLoadingComments, registerLoadingGate]);

  const handleSubmitComment = async () => {
    if (!card || !card.id || !commentContent.trim()) return;

    // Deduplication: Check if already submitting (ref is faster than state)
    if (isSubmittingRef.current) {
      logger.debug(
        '[TaskEditPopup] Prevented duplicate comment submission - already submitting'
      );
      return;
    }

    // Deduplication: Check if content is identical to last submission
    const trimmedContent = commentContent.trim();
    if (trimmedContent === lastSubmittedContentRef.current) {
      logger.debug(
        '[TaskEditPopup] Prevented duplicate comment submission - identical content'
      );
      return;
    }

    // Lock submission immediately (ref is sync; isSubmittingComment from hook is async)
    isSubmittingRef.current = true;

    const result = await executeCreateComment({
      task_id: card.id,
      content: trimmedContent,
    });

    if (result.code === ResponseCode.SUCCESS && result.data) {
      // Track last submitted content for deduplication (2s window)
      lastSubmittedContentRef.current = trimmedContent;
      setTimeout(() => {
        lastSubmittedContentRef.current = '';
      }, 2000);

      // Clear input and reset to first page
      setCommentContent('');
      setCommentsOffset(0);
    }

    // Unlock submission
    isSubmittingRef.current = false;
  };

  const handleLoadMoreComments = async () => {
    if (!card || !card.id || isLoadingComments) return;
    setCommentsOffset(prev => prev + 5); // TQ fetches new page via key change
  };

  const handleDeleteCard = async () => {
    if (!card?.id || !boardId) return;

    const result = await executeDeleteCard({
      task_id: card.id,
      board_id: boardId,
    });

    if (result.code === ResponseCode.SUCCESS) {
      onClose();
      onCardUpdated();
    } else if (result.code === ResponseCode.FORBIDDEN) {
      toast.error('You do not have permission to delete this card');
    }
  };

  const handleSave = async () => {
    // Double-save guard (keyboard Enter can bypass AsyncButton's built-in guard)
    if (isSavingRef.current) return;

    // Validate title
    if (!title.trim()) {
      setError('Title cannot be empty');
      return;
    }

    // Edge case #1: empty statuses guard
    if (isCreateMode && statuses.length === 0) {
      setError(
        'This board has no columns. Add a column before creating cards.'
      );
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);
    setErrorRetry(null);

    try {
      // Prepare metadata
      const metadata: Record<string, unknown> = {
        // Keep any other metadata fields here
      };

      // Parse estimatedPomodoros as integer
      let parsedPomodoros: number | undefined;
      if (estimatedPomodoros.trim() !== '') {
        const poms = parseInt(estimatedPomodoros, 10);
        if (!isNaN(poms) && poms > 0) {
          parsedPomodoros = poms;
        }
      }

      // Parse actualPomodoros as integer
      let parsedActualPomodoros: number | undefined;
      if (actualPomodoros.trim() !== '') {
        const poms = parseInt(actualPomodoros, 10);
        if (!isNaN(poms) && poms > 0) {
          parsedActualPomodoros = poms;
        }
      }

      if (isCreateMode) {
        // Create mode - need boardId
        if (!boardId) {
          setError('Board ID is missing');
          return;
        }

        // Guard: need at least one status column
        if (statuses.length === 0) {
          setError('No statuses available');
          return;
        }

        // Get the leftmost status (lowest position) for new cards
        const leftmostStatus = statuses.reduce(
          (min, status) => (status.position < min.position ? status : min),
          statuses[0]
        );

        // Call CREATE_CARD action
        const result = await executeCreateCard({
          board_id: boardId,
          status_id: leftmostStatus.id!,
          title: title.trim(),
          description: description.trim(),
          metadata,
          priority: priority || undefined,
          estimated_pomodoros: parsedPomodoros,
          start_date: startDate || undefined,
          deadline: deadline ? localDatetimeInputToIso(deadline) : undefined,
        });

        if (result.code === ResponseCode.SUCCESS) {
          onCardUpdated();
          onClose();
        } else {
          setError('Failed to create card. Please try again.');
          setErrorRetry(() => () => handleSave());
        }
      } else {
        // Edit mode - need card.id
        if (!card || !card.id) {
          setError('Card ID is missing');
          return;
        }

        // Call UPDATE_CARD action (automatically shows toast)
        // For deadline: empty string means "clear", a value means "set to this RFC3339 timestamp".
        // Only send the field if it has changed, so we don't accidentally clear an unmodified one.
        const deadlineChanged = deadline !== initialDeadline;
        const result = await executeUpdateCard({
          task_id: card.id,
          title: title.trim(),
          description: description.trim(),
          metadata,
          priority: priority || undefined,
          estimated_pomodoros: parsedPomodoros,
          actual_pomodoros: parsedActualPomodoros,
          start_date: startDate || undefined,
          deadline: deadlineChanged
            ? deadline
              ? localDatetimeInputToIso(deadline)
              : ''
            : undefined,
        });

        if (result.code === ResponseCode.SUCCESS) {
          onCardUpdated();
          onClose();
        } else {
          setError('Failed to update card. Please try again.');
          setErrorRetry(() => () => handleSave());
        }
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // In create mode, we don't need a card. In edit mode, we do.
  if (!isCreateMode && !card) return null;

  // Detect if any field has changed from initial values
  const hasChanges =
    title.trim() !== initialTitle ||
    description.trim() !== initialDescription ||
    priority !== initialPriority ||
    estimatedPomodoros !== initialEstimatedPomodoros ||
    actualPomodoros !== initialActualPomodoros ||
    startDate !== initialStartDate ||
    deadline !== initialDeadline;

  const handleCancel = () => {
    if (hasChanges && !isSaving) {
      unsavedChangesAlert.show({
        type: 'warning',
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Are you sure you want to discard them?',
        buttons: [
          {
            label: 'Keep Editing',
            variant: 'outline',
            onClick: () => {},
          },
          {
            label: 'Discard Changes',
            variant: 'destructive',
            onClick: () => {
              setError(null);
              setErrorRetry(null);
              onClose();
            },
          },
        ],
      });
      return;
    }
    setError(null);
    setErrorRetry(null);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleCancel}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col overflow-hidden gap-0 p-0">
          <div className="flex-1 overflow-y-auto">
            {/* Error Message */}
            {error && (
              <div
                className="flex items-center gap-3 p-4 m-6 mb-0 rounded-lg border"
                style={{
                  backgroundColor: withOpacity(colors.error, opacity.soft),
                  borderColor: withOpacity(colors.error, opacity.medium),
                  color: colors.error,
                }}
              >
                <Icon
                  name={IconName.ALERT_CIRCLE}
                  size="sm"
                  className="flex-shrink-0"
                />
                <span className="text-sm font-medium">
                  {error}
                  {errorRetry && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={errorRetry}
                        className="underline font-semibold hover:opacity-70"
                      >
                        Retry
                      </button>
                    </>
                  )}
                </span>
              </div>
            )}

            {/* Frozen / Forbidden Banner */}
            {(isFrozen || isEditForbidden) && (
              <div
                className="flex items-center gap-3 p-4 m-6 mb-0 rounded-lg border"
                style={{
                  backgroundColor: withOpacity(
                    colors.text.tertiary,
                    opacity.soft
                  ),
                  borderColor: withOpacity(
                    colors.text.tertiary,
                    opacity.medium
                  ),
                  color: colors.text.secondary,
                }}
              >
                <Icon
                  name={IconName.INFO}
                  size="sm"
                  className="flex-shrink-0"
                />
                <span className="text-sm font-medium">
                  {isCommentAllowed
                    ? 'Card content is synced from the cohort and cannot be edited. You can add comments below.'
                    : isFrozenFromPastWeek
                      ? 'This card is from a past week and cannot be edited.'
                      : 'This card cannot be edited.'}
                </span>
              </div>
            )}

            {/* Edit Locked Banner */}
            {isStructuralLocked && !isFrozen && !isEditForbidden && (
              <div
                className="flex items-center gap-3 p-4 m-6 mb-0 rounded-lg border"
                style={{
                  backgroundColor: colors.warning.background,
                  borderColor: colors.warning.border,
                  color: colors.warning.text,
                }}
              >
                <Icon
                  name={IconName.INFO}
                  size="sm"
                  className="flex-shrink-0"
                />
                <span className="text-sm font-medium">
                  This column is locked. Only pomodoro counts can be edited.
                </span>
              </div>
            )}

            {/* Deleted Card Banner */}
            {isCardDeleted && card && (
              <div className="px-6 pt-6 pb-0">
                <DeletedEntityBanner
                  entityType="card"
                  deletedAt={
                    typeof card.deleted_at === 'string'
                      ? card.deleted_at
                      : card.deleted_at!.toISOString()
                  }
                  restoreAction={Action.RESTORE_TASK}
                  restoreInput={{ task_id: card.id }}
                  canRestore={isAdmin}
                  onRestored={onCardUpdated}
                />
              </div>
            )}

            {/* Group Assignment Creator Info Banner */}
            {isCohortCreatorCard && (
              <div className="flex items-start gap-3 px-6 pt-3 pb-0">
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border w-full"
                  style={{
                    backgroundColor: withOpacity(colors.blue, 0.05),
                    borderColor: withOpacity(colors.blue, 0.2),
                    color: colors.text.secondary,
                  }}
                >
                  <Icon
                    name={IconName.INFO}
                    size="sm"
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: colors.blue }}
                  />
                  <span className="text-xs">
                    Title, description, difficulty, and start date sync to all
                    group member cards when you save. Individual progress fields
                    (pomodoros) are managed per member.
                  </span>
                </div>
              </div>
            )}

            {/* Sticky Header */}
            <div
              className="sticky top-0 z-10 px-6 pt-6 pb-4"
              style={{ backgroundColor: colors.background }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label
                    className="text-sm font-semibold"
                    style={{ color: colors.text.secondary }}
                  >
                    Title *
                  </label>
                  {(isCohortCopy || isCohortCreatorCard) && (
                    <div
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: withOpacity(colors.blue, 0.1),
                        color: colors.blue,
                        border: `1px solid ${withOpacity(colors.blue, 0.3)}`,
                      }}
                    >
                      {isCohortCreatorCard ? 'Cohort — Source' : 'Cohort'}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!isCreateMode && card?.id && (
                    <ShareButton
                      entity={DeepLinkEntity.CARD}
                      id={card.id}
                      ariaLabel="Copy card link"
                    />
                  )}
                  {isAdmin && !isCreateMode && card?.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() =>
                        deleteConfirmAlert.show({
                          type: 'warning',
                          title: 'Delete Card',
                          message:
                            'Are you sure you want to delete this card? This action cannot be undone.',
                          buttons: [
                            { label: 'Cancel', onClick: () => {} },
                            {
                              label: 'Delete Card',
                              onClick: handleDeleteCard,
                              variant: 'destructive',
                            },
                          ],
                        })
                      }
                      disabled={isDeleting}
                      title="Delete card"
                    >
                      <Icon
                        name={IconName.TRASH}
                        size="sm"
                        style={{ color: colors.error }}
                      />
                    </Button>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border text-base transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border.default,
                  color: colors.text.primary,
                  ['--tw-ring-color' as string]: withOpacity(
                    colors.primary,
                    opacity.medium
                  ),
                }}
                placeholder="Enter card title"
                disabled={
                  isSaving ||
                  isReadOnly ||
                  isCohortContentLocked ||
                  isStructuralLocked
                }
                autoFocus
              />
            </div>

            {/* Scrollable Content */}
            <div className="px-6 pb-6 pt-2">
              <div className="space-y-6">
                {/* Description Field */}
                <div>
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border resize-none transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border.default,
                      color: colors.text.primary,
                      ['--tw-ring-color' as string]: withOpacity(
                        colors.primary,
                        opacity.medium
                      ),
                    }}
                    placeholder="Add a description..."
                    disabled={
                      isSaving ||
                      isReadOnly ||
                      isCohortContentLocked ||
                      isStructuralLocked
                    }
                  />
                </div>

                {/* BoardTaskPriority, Estimated Pomodoros & Actual Pomodoros - Side by Side */}
                <div
                  className={`grid grid-cols-1 gap-4 ${isCohortCreatorCard ? '' : 'sm:grid-cols-3'}`}
                >
                  {/* BoardTaskPriority Dropdown */}
                  <div>
                    <label
                      className="block text-sm font-semibold mb-3"
                      style={{ color: colors.text.secondary }}
                    >
                      BoardTaskPriority
                    </label>
                    <div className="relative">
                      <select
                        value={priority}
                        onChange={e => setPriority(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2 appearance-none"
                        style={{
                          height: sizing.input.lg,
                          backgroundColor: colors.background,
                          borderColor: colors.border.default,
                          color: colors.text.primary,
                          ['--tw-ring-color' as string]: withOpacity(
                            colors.primary,
                            opacity.medium
                          ),
                        }}
                        disabled={
                          isSaving ||
                          isReadOnly ||
                          isCohortContentLocked ||
                          isStructuralLocked
                        }
                      >
                        <option value="">None</option>
                        {Object.values(BoardTaskPriority).map(priorityValue => (
                          <option key={priorityValue} value={priorityValue}>
                            {priorityValue.charAt(0).toUpperCase() +
                              priorityValue.slice(1)}
                          </option>
                        ))}
                      </select>
                      <div
                        className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ color: colors.text.tertiary }}
                      >
                        <Icon name={IconName.CHEVRON_DOWN} size="sm" />
                      </div>
                    </div>
                  </div>

                  {/* Estimated Pomodoros with Custom Controls — hidden on creator card */}
                  {!isCohortCreatorCard && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-3"
                        style={{ color: colors.text.secondary }}
                      >
                        🍅 Estimated
                      </label>
                      <div
                        className="relative flex items-stretch"
                        style={{ height: sizing.input.lg }}
                      >
                        {/* Decrement Button - Full Height */}
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(estimatedPomodoros) || 0;
                            if (current > 0) {
                              setEstimatedPomodoros(String(current - 1));
                            }
                          }}
                          disabled={
                            isSaving ||
                            isReadOnly ||
                            !estimatedPomodoros ||
                            parseInt(estimatedPomodoros) <= 0
                          }
                          className="px-3 rounded-l-lg border transition-all hover:opacity-80 active:scale-95 flex items-center justify-center h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                            opacity:
                              isSaving ||
                              isReadOnly ||
                              !estimatedPomodoros ||
                              parseInt(estimatedPomodoros) <= 0
                                ? 0.4
                                : 1,
                          }}
                        >
                          <Icon name={IconName.MINUS} size="sm" />
                        </button>

                        {/* Number Input */}
                        <input
                          type="number"
                          value={estimatedPomodoros}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === '' || /^\d+$/.test(v)) {
                              const num = parseInt(v, 10);
                              setEstimatedPomodoros(
                                v === ''
                                  ? ''
                                  : String(Math.min(num, MAX_POMODORO_COUNT))
                              );
                            }
                          }}
                          min="0"
                          max={MAX_POMODORO_COUNT}
                          step="1"
                          className="flex-1 px-4 py-2.5 border-y text-center transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                            ['--tw-ring-color' as string]: withOpacity(
                              colors.primary,
                              opacity.medium
                            ),
                          }}
                          placeholder="0"
                          disabled={isSaving || isReadOnly}
                        />

                        {/* Increment Button - Full Height */}
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(estimatedPomodoros) || 0;
                            setEstimatedPomodoros(
                              String(Math.min(current + 1, MAX_POMODORO_COUNT))
                            );
                          }}
                          disabled={isSaving || isReadOnly}
                          className="px-3 rounded-r-lg border transition-all hover:opacity-80 active:scale-95 flex items-center justify-center h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                          }}
                        >
                          <Icon name={IconName.PLUS} size="sm" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Actual Pomodoros with Custom Controls — hidden on creator card and during card creation */}
                  {!isCohortCreatorCard && !isCreateMode && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-3"
                        style={{ color: colors.text.secondary }}
                      >
                        <span
                          style={{ filter: 'hue-rotate(120deg) saturate(1.5)' }}
                        >
                          🍅
                        </span>{' '}
                        Actual
                      </label>
                      <div
                        className="relative flex items-stretch"
                        style={{ height: sizing.input.lg }}
                      >
                        {/* Decrement Button - Full Height */}
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(actualPomodoros) || 0;
                            if (current > 0) {
                              setActualPomodoros(String(current - 1));
                            }
                          }}
                          disabled={
                            isSaving ||
                            isReadOnly ||
                            !actualPomodoros ||
                            parseInt(actualPomodoros) <= 0
                          }
                          className="px-3 rounded-l-lg border transition-all hover:opacity-80 active:scale-95 flex items-center justify-center h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                            opacity:
                              isSaving ||
                              isReadOnly ||
                              !actualPomodoros ||
                              parseInt(actualPomodoros) <= 0
                                ? 0.4
                                : 1,
                          }}
                        >
                          <Icon name={IconName.MINUS} size="sm" />
                        </button>

                        {/* Number Input */}
                        <input
                          type="number"
                          value={actualPomodoros}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === '' || /^\d+$/.test(v)) {
                              const num = parseInt(v, 10);
                              setActualPomodoros(
                                v === ''
                                  ? ''
                                  : String(Math.min(num, MAX_POMODORO_COUNT))
                              );
                            }
                          }}
                          min="0"
                          max={MAX_POMODORO_COUNT}
                          step="1"
                          className="flex-1 px-4 py-2.5 border-y text-center transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                            ['--tw-ring-color' as string]: withOpacity(
                              colors.primary,
                              opacity.medium
                            ),
                          }}
                          placeholder="0"
                          disabled={isSaving || isReadOnly}
                        />

                        {/* Increment Button - Full Height */}
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(actualPomodoros) || 0;
                            setActualPomodoros(
                              String(Math.min(current + 1, MAX_POMODORO_COUNT))
                            );
                          }}
                          disabled={isSaving || isReadOnly}
                          className="px-3 rounded-r-lg border transition-all hover:opacity-80 active:scale-95 flex items-center justify-center h-full"
                          style={{
                            backgroundColor: colors.background,
                            borderColor: colors.border.default,
                            color: colors.text.primary,
                          }}
                        >
                          <Icon name={IconName.PLUS} size="sm" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Start Date — editable by creator (fans out to sub-cards); locked on sub-cards */}
                <div>
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Start Date
                  </label>
                  {isSaving ||
                  isReadOnly ||
                  isStartDateDisabled ||
                  isCohortContentLocked ||
                  isStructuralLocked ? (
                    <div
                      className="w-full px-4 py-2.5 rounded-lg border text-sm"
                      style={{
                        height: sizing.input.lg,
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                        color: startDate
                          ? colors.text.primary
                          : colors.text.tertiary,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {startDate ? formatDate(startDate) : '—'}
                    </div>
                  ) : (
                    <input
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      min={
                        isCreateMode ? parseDateToLocalString(null) : undefined
                      }
                      className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2"
                      style={{
                        height: sizing.input.lg,
                        backgroundColor: colors.background,
                        borderColor: colors.border.default,
                        color: colors.text.primary,
                        ['--tw-ring-color' as string]: withOpacity(
                          colors.primary,
                          opacity.medium
                        ),
                      }}
                    />
                  )}
                </div>

                {/* Deadline (optional) — datetime; clear with the input's built-in clear button */}
                <div>
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Deadline{' '}
                    <span
                      className="font-normal"
                      style={{ color: colors.text.tertiary }}
                    >
                      (optional)
                    </span>
                  </label>
                  {isSaving ||
                  isReadOnly ||
                  isCohortContentLocked ||
                  isStructuralLocked ? (
                    <div
                      className="w-full px-4 py-2.5 rounded-lg border text-sm"
                      style={{
                        height: sizing.input.lg,
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                        color: deadline
                          ? colors.text.primary
                          : colors.text.tertiary,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {deadline ? formatDate(deadline) : '—'}
                    </div>
                  ) : (
                    <input
                      type="date"
                      value={deadline}
                      onChange={e => setDeadline(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus-visible:outline-none focus-visible:shadow-soft-md focus-visible:ring-2"
                      style={{
                        height: sizing.input.lg,
                        backgroundColor: colors.background,
                        borderColor: colors.border.default,
                        color: colors.text.primary,
                        ['--tw-ring-color' as string]: withOpacity(
                          colors.primary,
                          opacity.medium
                        ),
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Status (Read-only) - Only show in edit mode */}
              {!isCreateMode && currentStatus && (
                <div className="mt-6">
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Status
                  </label>
                  <div
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg border"
                    style={{
                      backgroundColor: colors.offWhite,
                      borderColor: colors.border.default,
                    }}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: currentStatus.color }}
                    />
                    <span
                      className="font-medium"
                      style={{ color: colors.text.primary }}
                    >
                      {currentStatus.name}
                    </span>
                    <Icon
                      name={IconName.TAG}
                      size="sm"
                      color={colors.text.tertiary}
                      className="ml-auto"
                    />
                  </div>
                </div>
              )}

              {/* Created By (Read-only) - Only show in edit mode */}
              {!isCreateMode && (
                <div className="mt-6">
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Created By
                  </label>
                  {isLoadingOrgUsers ? (
                    <div
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                        color: colors.text.tertiary,
                      }}
                    >
                      <Icon
                        name={IconName.LOADER}
                        size="sm"
                        className="animate-spin"
                      />
                      <span className="text-sm">Loading...</span>
                    </div>
                  ) : creatorUser ? (
                    <div
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg border"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                      }}
                    >
                      <Icon
                        name={IconName.USER}
                        size="sm"
                        color={colors.blue}
                      />
                      <span
                        className="flex-1 font-medium"
                        style={{ color: colors.text.primary }}
                      >
                        {creatorUser.display_name}{' '}
                        <span
                          className="font-normal"
                          style={{ color: colors.text.tertiary }}
                        >
                          (@{creatorUser.username})
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div
                      className="px-4 py-2.5 rounded-lg border text-sm"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                        color: colors.text.tertiary,
                      }}
                    >
                      Creator not found
                    </div>
                  )}
                </div>
              )}

              {/* Related Cards / Subcards - Only show in edit mode */}
              {!isCreateMode && card?.id && (
                <SubTasksSection
                  taskId={card.id}
                  cardTitle={title}
                  cardPriority={priority || undefined}
                  statuses={statuses}
                  boardId={card.board_id}
                  onSubcardsCreated={newCards => {
                    onSubcardsCreated?.(newCards);
                    onClose();
                  }}
                  isFrozen={isReadOnly}
                  isFrozenFromPastWeek={isFrozenFromPastWeek}
                  onOpenCard={handleOpenCard}
                />
              )}

              {/* Comments Section - Only show in edit mode */}
              {!isCreateMode && (
                <div className="mt-6">
                  <label
                    className="block text-sm font-semibold mb-3"
                    style={{ color: colors.text.secondary }}
                  >
                    Comments
                  </label>

                  {/* Comments Error State */}
                  {isCommentsError && (
                    <div
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                        color: colors.error,
                      }}
                    >
                      Failed to load comments.{' '}
                      <button
                        type="button"
                        onClick={() =>
                          queryClient.invalidateQueries({
                            queryKey: queryKeys.comments.forTask(card!.id!),
                          })
                        }
                        className="underline font-medium hover:opacity-70"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {/* Comment Input */}
                  {(!isReadOnly || isCommentAllowed) && (
                    <MentionInput
                      value={commentContent}
                      onChange={setCommentContent}
                      onSubmit={handleSubmitComment}
                      orgUsers={orgUsers}
                      disabled={isSubmittingComment}
                      isLoading={isSubmittingComment}
                      maxLength={1000}
                    />
                  )}

                  {/* Comments List */}
                  <div className="mt-4 max-h-96 overflow-y-auto">
                    <CommentsList
                      comments={comments}
                      isLoading={isLoadingComments && comments.length === 0}
                      hasMore={hasMoreComments}
                      onLoadMore={handleLoadMoreComments}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div
            className="flex justify-end gap-3 px-6 py-5 border-t flex-shrink-0"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border.default,
            }}
          >
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="px-5"
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <AsyncButton
                onClick={handleSave}
                disabled={isCreateMode ? !title.trim() : !hasChanges}
                isLoading={isSaving}
                icon={isCreateMode ? IconName.PLUS : IconName.SAVE}
                loadingText={isCreateMode ? 'Creating...' : 'Saving...'}
                className="px-5"
              >
                {isCreateMode ? 'Create Card' : 'Save Changes'}
              </AsyncButton>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes confirmation */}
      <Alert {...unsavedChangesAlert.props} />

      {/* Delete card confirmation */}
      <Alert {...deleteConfirmAlert.props} />
    </>
  );
}
